import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Download, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { daysToMs } from '@/lib/time';
import { isBrowser } from '@/lib/utils';

const STORAGE_KEY = 'ru24_pwa_install_dismissed_v1';
const DISMISS_TTL_MS = daysToMs(14); // 14 days

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
}

function readDismissed(): boolean {
  /* istanbul ignore if */
  if (!isBrowser()) return false;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const parsed = JSON.parse(raw) as { at?: number };
    if (!parsed?.at) return false;
    if (Date.now() - parsed.at > DISMISS_TTL_MS) {
      window.localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return true;
  } catch (error) {
    return false;
  }
}

function writeDismissed(): void {
  /* istanbul ignore if */
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now() }));
  } catch (error) {
    // localStorage may be unavailable (private mode, quota); ignore silently
  }
}

export function PWAInstallPrompt() {
  const { t } = useTranslation();
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    /* istanbul ignore if */
    if (!isBrowser()) return;
    setDismissed(readDismissed());

    const onBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setDeferredPrompt(event as BeforeInstallPromptEvent);
    };

    const onAppInstalled = () => {
      setInstalled(true);
      setDeferredPrompt(null);
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstallPrompt);
    window.addEventListener('appinstalled', onAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstallPrompt);
      window.removeEventListener('appinstalled', onAppInstalled);
    };
  }, []);

  if (!deferredPrompt || installed || dismissed) {
    return null;
  }

  const handleInstall = async () => {
    try {
      await deferredPrompt.prompt();
      const choice = await deferredPrompt.userChoice;
      if (choice.outcome === 'accepted') {
        setInstalled(true);
      }
    } catch (error) {
      // ignore — the browser may not support prompt() in some contexts
    } finally {
      setDeferredPrompt(null);
    }
  };

  const handleDismiss = () => {
    writeDismissed();
    setDismissed(true);
    setDeferredPrompt(null);
  };

  const labels = {
    title: t('pwa.installTitle'),
    description: t('pwa.installDescription'),
    install: t('pwa.installAction'),
    later: t('pwa.installLater'),
  };

  return (
    <div
      role="region"
      aria-label={labels.title}
      data-testid="pwa-install-prompt"
      className="fixed inset-x-0 bottom-0 z-50 border-t border-border bg-background/95 p-3 shadow-lg backdrop-blur supports-[backdrop-filter]:bg-background/80 sm:p-4"
    >
      <div className="mx-auto flex max-w-3xl flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="flex items-start gap-3">
          <div
            aria-hidden="true"
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary"
          >
            <Download className="h-5 w-5" />
          </div>
          <div className="text-sm">
            <p className="font-medium leading-tight">{labels.title}</p>
            <p className="mt-1 leading-snug text-muted-foreground">{labels.description}</p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
          <Button variant="ghost" size="sm" onClick={handleDismiss} aria-label={labels.later}>
            <X className="h-4 w-4" aria-hidden="true" />
            {labels.later}
          </Button>
          <Button size="sm" onClick={handleInstall} data-testid="pwa-install-action">
            <Download className="h-4 w-4" aria-hidden="true" />
            {labels.install}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default PWAInstallPrompt;
